import { useAuth } from "helpers/authContext";
import { Link } from "react-router-dom";
import { uploadImageToSteem } from "helpers/imageUpload";
import { formatMB, uploadVideoToIPFS, detectVideoOrientation } from "helpers/videoUpload";
import React, { useEffect, useRef, useState } from "react";
import { Alert, FormGroup } from "reactstrap";
import { useToast } from "./ui/use-toast";
import { clearUploadState, getUploadState } from "helpers/UploadState";
import { uploadManager } from "helpers/UploadContext";
import { getVideo } from "helpers/UploadDB";

type Props = {
  register: any;
  setValue: (name: string, value: any) => void;
  watch: (name: string) => any;
  IpfsAccount: any;
  placeholder?: string;
  type?: string;
  uploading?: boolean;
  setUploading?: (state: boolean) => void;
  paused?: boolean;
  setPaused?: (state: boolean) => void;
  setOrientation?: any;
  cancelUploadState?:boolean;
};
const DAYS_FOR_RESET = parseInt(process.env.DAYS_FOR_RESET) || 30;
export default function FullEditor({
  register,
  setValue,
  watch,
  IpfsAccount,
  placeholder = "Description",
  type = "description-input",
  uploading,
  setUploading,
  paused,
  setPaused,
  setOrientation,
  cancelUploadState
}: Props) { 
  const { toast } = useToast();
  const { user, autologin } = useAuth();
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  
  const [pausedReason, setPausedReason] = useState("");
  const videoInputRef = useRef<HTMLInputElement>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [size, setSize] = useState(0);
  const [AvailableQuota, setAvailableQuota] = useState(5000);
  const [UsedQuota, setUsedQuota] = useState(0);
  // const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const descriptionValue: string = watch("description") || "";
  const { ref, ...rest } = register("description");
  useEffect(() => {
    const state = getUploadState();
    if (!state) return;

    async function initUpload() {
      try {
        const file = await getVideo(state.uploadId);
        if (!file) return;

        setSize(file.size);
        setUploading && setUploading(true);

        const unsubscribe = uploadManager.subscribe(state.uploadId, (data) => {
          setUploadProgress(data.progress);
          setLoaded(data.loaded);
          if (data.progress == 100) { 
              console.log("upload finished, processing..."); 
              let checker = setInterval(() => {
                  let newState = getUploadState();
                  if (newState?.cid) { 
                    const el = editorRef.current;
                    if (!el) return;
 
                    const finalToken = newState.url ? `\n[IPFS Video](${newState.url})\n` : ""; 
                    setText(el.value + finalToken); 
                    // setOrientation && setOrientation("clip");
                    const idx = el.value.indexOf(finalToken);
                    const after = idx >= 0 ? idx + finalToken.length : el.value.length;
                    requestAnimationFrame(() => {
                      el.selectionStart = el.selectionEnd = after;
                      el.focus();
                    });
                      // setLink(newState.url);
                      // setUploadResult(`✅ Uploaded! CID: ${newState.cid}`);
                      toast({
                          title: "✅ Uploaded!",
                          description: `You can see the content using this CID: ${newState.cid}`,
                          variant:"wild_green"
                      }); 
                      clearUploadState();
                      setUploading && setUploading(false); 
                      setUploadProgress(0);
                      clearInterval(checker);
                  }
              }, 3000);
          }
        });

        return unsubscribe;
      } catch (err) {
        console.error("Failed to restore upload state", err);
      }
    }

    let unsubscribe: any;

    initUpload().then((fn) => {
      unsubscribe = fn;
    });

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);
  useEffect(() => {
    if (cancelUploadState) {
      setUploadProgress(0);
      setUploading && setUploading(false); 
    }
  }, [cancelUploadState]);
  useEffect(() => {
    const state = getUploadState();
    if (!state) return;

    if (state.paused) {
      setPaused(true);
      setPausedReason(state.reason || "Upload paused");
    }
  }, []);
  useEffect(() => {
      const validFiles = Array.isArray(IpfsAccount.files)
          ? IpfsAccount.files.filter(item => IpfsAccount.plan.includes("plus") ? Number(item.days_ago) <= DAYS_FOR_RESET:true)
          : []; 
      const totalSize = Array.isArray(validFiles)
      ? validFiles.reduce((acc, item) => acc + Number(item.size || 0), 0)
      : 0;
      const available_quota = (IpfsAccount.quota*1e6)-totalSize; 
      setAvailableQuota(available_quota);
      setUsedQuota(totalSize); 
  }, [IpfsAccount]);
  // ========== helpers para textarea ========== 
  const setText = (newText: string) => {
    const el = editorRef.current;
    if (!el) return;
    el.value = newText;
    setValue("description", newText);
  };

  const insertTextAtSelection = (insertText: string) => {
    const el = editorRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const base = el.value ?? "";

    const newText = base.slice(0, start) + insertText + base.slice(end);
    setText(newText);

    const cursorPos = start + insertText.length;
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = cursorPos;
      el.focus();
    });
  };

  const wrapSelection = (prefix: string, suffix: string) => {
    const el = editorRef.current; 
    
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const base = el.value ?? "";
    const selected = base.substring(start, end);

    const newText = base.slice(0, start) + prefix + selected + suffix + base.slice(end);
    setText(newText); 
    
    requestAnimationFrame(() => {
      if (start !== end) {
        el.selectionStart = start + prefix.length;
        el.selectionEnd = start + prefix.length + selected.length;
      } else {
        const caret = start + prefix.length;
        el.selectionStart = el.selectionEnd = caret;
      }
      el.focus();
    });
  };

  // ========== toolbar ==========
  const applyCustomCommand = (type: string, title_level = 1) => {
    switch (type) {
      case "bold":       return wrapSelection("**", "**");
      case "italic":     return wrapSelection("*", "*");
      case "blockquote": return wrapSelection("> ", "\n");
      case "link":       return wrapSelection("[", "]()");
      case "title":      return wrapSelection("\n" + "#".repeat(title_level) + " ", "\n");
      case "sup":        return wrapSelection("<sup>", "</sup>");
      case "sub":        return wrapSelection("<sub>", "</sub>");
      case "center":     return wrapSelection(`<div class="text-center">\n\n`, `\n\n</div>`);
      case "left":       return wrapSelection(`<div class="text-left">\n\n`, `\n\n</div>`);
      case "right":      return wrapSelection(`<div class="text-right">\n\n`, `\n\n</div>`);
      case "justify":    return wrapSelection(`<div class="text-justify">\n\n`, `\n\n</div>`);
      default: return;
    }
  };

  // ========== marcadores ==========
  const insertPlaceholder = (placeholderId: string): string => {
    const marker = `[Uploading ${placeholderId}...]`;
    insertTextAtSelection(marker);
    return marker;
  };

  // ========== imagen ==========
  const insertImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const placeholderId = `img-${Date.now()}`;
    const marker = insertPlaceholder(placeholderId);

    const url = await uploadImageToSteem({
      file,
      user,
      autologin,
      setErrorMessage: (msg: string) => setErrorMessage(msg),
    });

    const el = editorRef.current;
    if (!el) return;

    const finalToken = url ? `\n![Image](${url})\n` : "";
    setText(el.value.replace(marker, finalToken));

    // posicionar cursor tras el token
    const idx = el.value.indexOf(finalToken);
    const after = idx >= 0 ? idx + finalToken.length : el.value.length;
    requestAnimationFrame(() => {
      el.selectionStart = el.selectionEnd = after;
      el.focus();
    });
  };

    // ========== video ==========
    const insertVideo = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      const state = getUploadState();

      if (state && uploading) {
        setErrorMessage("Another upload is in progress");
        return;
      }

      const placeholderId = `vid-${Date.now()}`;
      const marker = insertPlaceholder(placeholderId);

      const validTypes = ["video/mp4", "video/quicktime"];
      const maxSize = IpfsAccount.max_size * 1024 * 1024;
      // Archivos que cuentan según la regla
      setSize(file.size);
      console.log(`available: (${AvailableQuota} mb/${IpfsAccount.quota} mb) Plan: ${IpfsAccount.plan}`);
      if (!validTypes.includes(file.type)) return setErrorMessage("Only MP4 or MOV files are allowed.");
      if (file.size > maxSize)           return setErrorMessage(`File exceeds the ${IpfsAccount.max_size} MB limit.`);
      if (AvailableQuota < file.size)   return setErrorMessage(`You have exceed your uploading quota. Available: ${AvailableQuota} File size: ${file.size}`);

      try {
        // setIsUploading(true);
        setUploading && setUploading(true);
        console.log("Uploading started...");
        
        const res: any = await uploadVideoToIPFS({
          file,
          user,
          setProgress: (p: number) => {
            console.log("Video upload progress:", p);
            setUploadProgress(p)
          },
          setPaused, setPausedReason, setLoaded
        });
        const url = res?.data?.url;

        const el = editorRef.current;
        if (!el) return;

        // const finalToken = url ? `\n[IPFS Video](${url})\n` : "";
        // setText(el.value.replace(marker, finalToken));
        const finalToken = url ? `\n[IPFS Video](${url})\n` : "";

        if (el.value.includes(marker)) {
          setText(el.value.replace(marker, finalToken));
        } else {
          // el placeholder se perdió (refresh)
          setText(el.value + finalToken);
        }
        // Detect orientation
        if (setOrientation) {
          const orientation = await detectVideoOrientation(file);
          setOrientation(orientation);
        }
        
        const idx = el.value.indexOf(finalToken);
        const after = idx >= 0 ? idx + finalToken.length : el.value.length;
        requestAnimationFrame(() => {
          el.selectionStart = el.selectionEnd = after;
          el.focus();
        });
      } catch {
        setErrorMessage("Fail to upload video.");
      }finally{
        setUploading && setUploading(false);
        console.log("Uploading finished...");
        setUploadProgress(0);
      }
    }; 
    const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const items = Array.from(e.clipboardData?.items || []);
      const fileItem = items.find(it => it.kind === "file");
      if (!fileItem) return; // si no hay archivo, dejar paste normal
          const state = getUploadState();

      if (state && uploading) {
        setErrorMessage("Another upload is in progress");
        return;
      }
      e.preventDefault();
      const file = fileItem.getAsFile();
      if (!file) return;

      if (file.type.startsWith("image/")) {
          // === SUBIR IMAGEN DIRECTO ===
          const placeholderId = `img-${Date.now()}`;
          const marker = insertPlaceholder(placeholderId);

          uploadImageToSteem({
          file,
          user,
          autologin,
          setErrorMessage,
          }).then(url => {
          const el = editorRef.current;
          if (!el) return;
          const finalToken = url ? `\n![Image](${url})\n` : "";
          setText(el.value.replace(marker, finalToken));
          }).catch(() => {
          setErrorMessage("Fail to upload image.");
          });
      }

      if (file.type.startsWith("video/")) {
          // === SUBIR VIDEO DIRECTO ===
          const placeholderId = `vid-${Date.now()}`;
          const marker = insertPlaceholder(placeholderId);

          const validTypes = ["video/mp4", "video/quicktime"];
          const maxSize = IpfsAccount.max_size * 1024 * 1024;
          setSize(file.size);
          
          console.log(`available: (${AvailableQuota} mb/${IpfsAccount.quota} mb) Plan: ${IpfsAccount.plan}`);
          if (!validTypes.includes(file.type)) return setErrorMessage("Only MP4 or MOV files are allowed."); 
          if (file.size > maxSize) return setErrorMessage(`File exceeds the ${IpfsAccount.max_size} MB limit.`); 
          if (AvailableQuota < file.size) return setErrorMessage(`You have exceed your uploading quota. Available: ${AvailableQuota} File size: ${file.size}`); 
          setUploading && setUploading(true);
          console.log("Uploading started...");
          uploadVideoToIPFS({
            file,
            user,
            setProgress: (p: number) => {
              console.log("Video upload progress:", p);
              setUploadProgress(p);
            },
            setPaused, setPausedReason, setLoaded
          }).then(async (res: any) => {
          setUploading && setUploading(false);
          setUploadProgress(0);
          console.log("Uploading finished...");
          const url = res?.data?.url;
          const el = editorRef.current;
          if (!el) return;
          // const finalToken = url ? `\n[IPFS Video](${url})\n` : "";
          // setText(el.value.replace(marker, finalToken));
          const finalToken = url ? `\n[IPFS Video](${url})\n` : "";

          if (el.value.includes(marker)) {
            setText(el.value.replace(marker, finalToken));
          } else {
          // el placeholder se perdió (refresh)
            setText(el.value + finalToken);
          }
          if (setOrientation) {
            const orientation = await detectVideoOrientation(file);
            setOrientation(orientation);
          }
          // setOrientation && setOrientation("clip");
          }).catch(() => {
            setErrorMessage("Fail to upload video.");
            setUploading && setUploading(false);
          });
      }
    };

    const handleDragOver = (e: React.DragEvent<HTMLTextAreaElement>) => {
        e.preventDefault();
        e.stopPropagation();
    };
    const handleDrop = async (e: React.DragEvent<HTMLTextAreaElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const state = getUploadState();

        if (state && uploading) {
          setErrorMessage("Another upload is in progress");
          return;
        }
        const files = Array.from(e.dataTransfer.files || []);
        if (files.length === 0) {
            setErrorMessage("No valid files dropped.");
            return;
        }

        const el = editorRef.current;
        if (!el) return;

        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        let content = el.value;

        // Guardamos dónde vamos insertando
        let insertionPoint = start;

        // Lista de archivos con su marcador
        const markers: { id: string; type: "image" | "video"; file: File }[] = [];

        // 1️⃣ Insertamos marcadores en la posición actual
        for (const file of files) {
            let id = "";
            let marker = "";

            if (file.type.startsWith("image/")) {
              id = `img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
              marker = `\n[Uploading ${id}...]\n`;
              markers.push({ id, type: "image", file });
            } else if (file.type.startsWith("video/")) {
              id = `vid-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
              marker = `\n[Uploading ${id}...]\n`;
              markers.push({ id, type: "video", file });
            } else {
              setErrorMessage("Only images or videos are allowed.");
              continue;
            }

            content = content.slice(0, insertionPoint) + marker + content.slice(end);
            insertionPoint += marker.length;
        }

        setText(content);

        // 2️⃣ Restauramos cursor al final del último marcador
        requestAnimationFrame(() => {
            el.selectionStart = el.selectionEnd = insertionPoint;
            el.focus();
        });

        // 3️⃣ Subimos y reemplazamos cada marcador por el token definitivo
        for (const { id, type, file } of markers) {
            try {
              if (type === "image") {
                  const url = await uploadImageToSteem({
                  file,
                  user,
                  autologin,
                  setErrorMessage: (msg: string) => setErrorMessage(msg),
                  });
                  if (url) {
                  content = content.replace(`[Uploading ${id}...]`, `![Image](${url})`);
                  }
              } else {
                const maxSize = IpfsAccount.max_size * 1024 * 1024;
                
                console.log(`available: (${AvailableQuota} mb/${IpfsAccount.quota} mb) Plan: ${IpfsAccount.plan}`);
                setSize(file.size);
                if (
                  file.size > maxSize ||
                  !["video/mp4", "video/quicktime"].includes(file.type)
                  ) {
                  setErrorMessage(`Invalid video file (max ${IpfsAccount.max_size}, MP4/MOV only).`);
                  continue;
                }
                if (AvailableQuota < file.size) {
                  setErrorMessage(`You have exceed your uploading quota. Available: ${AvailableQuota} File size: ${file.size}`);
                  continue;
                }
                setUploading && setUploading(true);
                console.log("Uploading started...");
                const res: any = await uploadVideoToIPFS({
                  file,
                  user,
                  setProgress: (p: number) => {
                    console.log("Video upload progress:", p);
                    setUploadProgress(p)
                  },
                  setPaused, setPausedReason, setLoaded
                });
                console.log(res);
                
                const url = res?.data?.url;
                console.log(url);
                
                if (url) {
                  const placeholder = `[Uploading ${id}...]`;
                  const finalToken = `[IPFS Video](${url})`;

                  if (content.includes(placeholder)) {
                    content = content.replace(placeholder, finalToken);
                  } else {
                    // si el placeholder se perdió (refresh)
                    content += `\n${finalToken}\n`;
                  }
                }
                setUploading && setUploading(false);
                setUploadProgress(0);
                if (setOrientation) {
                  const orientation = await detectVideoOrientation(file);
                  setOrientation(orientation);
                }
                // setOrientation && setOrientation("clip");
                console.log("Uploading finished...");
              } 
              setText(content);
            } catch {
              setErrorMessage(`Failed to upload ${type}.`);
            }
        }
    };
    useEffect(() => {
      if (errorMessage) {
          toast({
              title: "Ops! Something have failed.",
              description: errorMessage,
              variant:"rose_red"
          }); 
      }
    }, [errorMessage]); 
    return (
        <FormGroup> 
        {((UsedQuota / (IpfsAccount.quota * 1e6)) > 0.75 && (UsedQuota / (IpfsAccount.quota * 1e6)) < 0.98) && (
            <Alert color='primary'>
                You're over 75% of your storage quota. Upgrade your plan to continue uploading files without limitations. <Link to="/settings">Learn more here.</Link>
            </Alert>
        )}
        {((UsedQuota / (IpfsAccount.quota * 1e6)) >= 0.98) && (
            <Alert color='danger'>
                You have not available quota to upload files. Upgrade your plan now. <Link to="/settings">Learn more here.</Link>
            </Alert>
        )}
        {(uploadProgress > 0 && uploadProgress < 100 && !paused) && <span>Uploading video: {uploadProgress} % ({formatMB(loaded)} MB/{formatMB(size)} MB)</span>}
        {(uploadProgress == 100 && !paused) && <span>Processing...</span>}
        {(uploadProgress > 0 && paused) && <span className="color-red">{pausedReason}</span>}
        <div className="editor-wrapper fancy-input">
            {/* Toolbar */}
            <div className="editor-toolbar">
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("link")}><i className="fas fa-link" /></button>
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("bold")}><i className="fas fa-bold" /></button>
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("italic")}><i className="fas fa-italic" /></button>
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("blockquote")}><i className="fas fa-quote-left" /></button>
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("sup")}><b><sup>Aa</sup></b></button>
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("sub")}><b><sub>Aa</sub></b></button>
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("left")}><i className="fas fa-align-left" /></button>
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("center")}><i className="fas fa-align-center" /></button>
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("justify")}><i className="fas fa-align-justify" /></button>
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("right")}><i className="fas fa-align-right" /></button>
            <button type="button" disabled={uploading} onClick={() => imageInputRef.current?.click()}><i className="fas fa-image" /></button>
            <input ref={imageInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={insertImage} />
            <button type="button" disabled={uploading} onClick={() => videoInputRef.current?.click()}><i className="fas fa-video" /></button>
            <input ref={videoInputRef} type="file" accept=".mp4,.mov" style={{ display: "none" }} onChange={insertVideo} />
            </div>

            {/* Titles */}
            <div className="editor-toolbar">
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("title", 1)}><b>H1</b></button>
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("title", 2)}><b>H2</b></button>
            <button type="button" disabled={uploading} onClick={() => applyCustomCommand("title", 3)}><b>H3</b></button>
            </div>

            {/* Textarea */}
            <textarea
                className={"form-control full-editor-textarea " + type}
                name="description"
                placeholder={placeholder} 
                ref={(el) => { editorRef.current = el; ref(el); }}
                {...rest}
                value={descriptionValue}
                onChange={(e) => setValue("description", e.target.value)}
                onPaste={handlePaste}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                style={{
                    border: "0",
                    minHeight: "200px",
                    maxHeight: "300px",
                    outline: "none",
                    whiteSpace: "pre-wrap",
                    overflowY: "auto"
                }}
            />
        </div>
        </FormGroup>
    );
}
